// BeneAIMessageList — BeneAI-01 + BeneAI-02.
//
// Walks the transcript and renders each assistant message as an
// ordered list of typed blocks: text, tool_use, tool_result,
// pending_confirmation, receipt. User messages stay as plain
// bubbles. BeneAI-02 introduces the structured cards while leaving
// the BeneAI-01 empty + pending bubble in place.

import { useEffect, useRef } from "react";

import { BeneAIMarkdown } from "./BeneAIMarkdown";
import { useBeneAIDrawer } from "./BeneAIDrawerContext";
import { BeneAIConfirmationCard } from "./cards/BeneAIConfirmationCard";
import { BeneAIReceiptCard } from "./cards/BeneAIReceiptCard";
import { BeneAIToolCallCard } from "./cards/BeneAIToolCallCard";
import { BeneAIToolResultCard } from "./cards/BeneAIToolResultCard";
import { parseAssistantContent } from "./parseAssistantContent";
import { useBeneAIConfirmation } from "./useBeneAIConfirmation";
import type { BeneAIAssistantBlock, BeneAIMessage } from "./types";


interface BeneAIMessageListProps {
  messages: BeneAIMessage[];
  isAwaitingAssistant: boolean;
}


export function BeneAIMessageList({
  messages, isAwaitingAssistant,
}: BeneAIMessageListProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isAwaitingAssistant]);

  if (messages.length === 0 && !isAwaitingAssistant) {
    return <BeneAIEmptyState />;
  }

  return (
    <div
      ref={scrollRef}
      data-testid="beneai-message-list"
      className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3"
    >
      {messages.map((m) => (
        <BeneAITranscriptRow
          key={`${m.message_id}-${m.sequence}`}
          msg={m}
        />
      ))}
      {isAwaitingAssistant && <BeneAIPendingBubble />}
    </div>
  );
}


function BeneAITranscriptRow({ msg }: { msg: BeneAIMessage }) {
  if (msg.role === "user") {
    return <BeneAIUserBubble msg={msg} />;
  }
  const blocks = parseAssistantContent(msg);
  if (blocks.length === 0) {
    return <BeneAIAssistantBubble text={msg.content || "(no response)"} />;
  }
  return (
    <div className="flex flex-col gap-2" data-message-id={msg.message_id}>
      {blocks.map((b, i) => (
        <BeneAIBlockRenderer
          key={`${msg.message_id}-block-${i}`} block={b}
        />
      ))}
    </div>
  );
}


function BeneAIBlockRenderer({ block }: { block: BeneAIAssistantBlock }) {
  const drawer = useBeneAIDrawer();
  const resolver = useBeneAIConfirmation();

  if (block.kind === "text") {
    return <BeneAIAssistantBubble text={block.text} />;
  }
  if (block.kind === "tool_use") {
    return <BeneAIToolCallCard block={block.block} />;
  }
  if (block.kind === "tool_result") {
    return <BeneAIToolResultCard block={block.block} />;
  }
  if (block.kind === "receipt") {
    return <BeneAIReceiptCard block={block.block} />;
  }
  if (block.kind === "error") {
    return <BeneAIErrorBubble message={block.message} />;
  }
  // pending_confirmation — look up the live drawer-state row so the
  // card disables its buttons during a Confirm / Cancel in-flight
  // call and after resolution (the persisted block keeps the
  // PENDING status forever).
  const state = drawer.confirmations[block.block.confirmation_id];
  const resolved = state ? state.status !== "pending" : false;
  return (
    <BeneAIConfirmationCard
      block={block.block}
      pending={state?.inFlight ?? false}
      resolved={resolved}
      onConfirm={(id) => { void resolver.confirm(id); }}
      onCancel={(id) => { void resolver.cancel(id); }}
    />
  );
}


function BeneAIUserBubble({ msg }: { msg: BeneAIMessage }) {
  return (
    <div
      data-testid="beneai-message-user"
      data-message-id={msg.message_id}
      className="max-w-[85%] self-end whitespace-pre-wrap break-words rounded-md bg-recessed px-3 py-2 text-sm leading-relaxed text-text-primary"
    >
      {msg.content}
    </div>
  );
}


function BeneAIAssistantBubble({ text }: { text: string }) {
  return (
    <div
      data-testid="beneai-message-assistant"
      className="max-w-[85%] self-start rounded-md bg-sage-tint px-3 py-2 text-sm leading-relaxed text-text-primary"
    >
      <BeneAIMarkdown body={text} />
    </div>
  );
}


function BeneAIErrorBubble({ message }: { message: string }) {
  return (
    <div
      data-testid="beneai-message-error"
      role="alert"
      className="max-w-[85%] self-start rounded-md border border-ember bg-ember-tint px-3 py-2 text-sm leading-relaxed text-text-primary"
    >
      {message}
    </div>
  );
}


function BeneAIPendingBubble() {
  return (
    <div
      data-testid="beneai-pending"
      className="inline-flex items-center gap-2 self-start rounded-md bg-sage-tint px-3 py-2 text-sm text-text-secondary"
    >
      <span className="inline-flex gap-1">
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal"
          style={{ animationDelay: "0ms" }}
        />
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal"
          style={{ animationDelay: "160ms" }}
        />
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal"
          style={{ animationDelay: "320ms" }}
        />
      </span>
      <span>BeneAI is thinking…</span>
    </div>
  );
}


function BeneAIEmptyState() {
  return (
    <div
      data-testid="beneai-empty-state"
      className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-6 text-sm text-text-secondary"
    >
      <p className="text-text-primary">
        Ask BeneAI to help with your day. It knows about:
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Accounts and Account Contacts</li>
        <li>Commitments — yours and your team's</li>
        <li>Playbooks and the work they spawn</li>
        <li>Touchpoints — meetings, calls, and notes</li>
        <li>Documents and files attached to Accounts</li>
        <li>Team members and their workload</li>
        <li>Dashboard analytics and pipeline questions</li>
      </ul>
      <p className="text-text-tertiary">
        Try “Show me overdue Commitments for the Magnolia account” or
        “Summarize Houston renewals this quarter.”
      </p>
    </div>
  );
}
